// Native (Capacitor) push registration for the mobile apps.
//
// On the web this is inert (isNativePlatform() is false). Inside the app it asks for
// notification permission, registers with FCM (Android) / APNs (iOS), sends the device
// token to the backend (`/users/me/device-push-token`), and deep-links on tap.
//
// The plugin is imported statically. A dynamic import() here breaks on Android: the
// Promise resolves to the Capacitor plugin proxy, whose `.then` gets probed and throws
// "PushNotifications.then() is not implemented on android". Importing on the web is
// harmless - it only touches the native bridge when methods are called, and every call
// here is guarded by isNativePlatform().

import { PushNotifications } from "@capacitor/push-notifications";

import { getNativePlatform, isNativePlatform } from "./platform";
import { API_BASE } from "./apiBase";

const FCM_TOKEN_KEY = "gc:nativePushToken";
let listenersBound = false;
// The plugin's "registration" event fires asynchronously after register(); stash the
// current auth token so the handler can authenticate the PUT.
let currentAuthToken = null;
// Optional app-supplied callback for pushes that arrive while the app is foregrounded
// (Android shows no tray notification then, so the app surfaces its own in-app cue).
let onForegroundPush = null;

// Register a handler invoked with { title, body, data } when a push arrives in the
// foreground. Pass null to clear. No-op relevance on web (listeners are native-only).
export function setForegroundPushHandler(fn) {
  onForegroundPush = typeof fn === "function" ? fn : null;
}

async function putToken(authToken, deviceToken) {
  if (!authToken || !deviceToken) return;
  try {
    const res = await fetch(`${API_BASE}/users/me/device-push-token`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ token: deviceToken, platform: getNativePlatform() || "android" }),
    });
    if (!res.ok) console.warn("[push] device-push-token PUT returned", res.status);
    try { localStorage.setItem(FCM_TOKEN_KEY, deviceToken); } catch {}
  } catch (e) {
    console.warn("[push] device-push-token PUT failed", e);
  }
}

async function deleteToken(authToken, deviceToken) {
  if (!authToken || !deviceToken) return;
  try {
    await fetch(`${API_BASE}/users/me/device-push-token?token=${encodeURIComponent(deviceToken)}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${authToken}` },
    });
  } catch {}
}

async function bindListeners() {
  if (listenersBound) return;
  listenersBound = true;
  // Fires with the device token after register() (and again if it rotates).
  await PushNotifications.addListener("registration", (t) => {
    putToken(currentAuthToken, t && t.value);
  });
  await PushNotifications.addListener("registrationError", (err) => {
    console.warn("[push] registrationError", JSON.stringify(err));
  });
  // User tapped a notification: deep-link to the thread the backend put in `data.url`.
  await PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
    const url =
      (action && action.notification && action.notification.data && action.notification.data.url) ||
      "/messages";
    try { window.location.assign(url); } catch {}
  });
  // Push arrived while the app is open: Android suppresses the tray notification, so let
  // the app show an in-app cue instead.
  await PushNotifications.addListener("pushNotificationReceived", (notification) => {
    if (typeof onForegroundPush !== "function") return;
    try {
      onForegroundPush({
        title: notification && notification.title,
        body: notification && notification.body,
        data: (notification && notification.data) || {},
      });
    } catch {}
  });
}

// Register or de-register this device based on the user's notification preference.
// Mirrors syncBrowserPushSubscription(token, enabled) for the native side.
export async function syncNativePush(authToken, enabled) {
  if (!isNativePlatform()) return;
  currentAuthToken = authToken || null;

  if (!enabled || !authToken) {
    let stored = null;
    try { stored = localStorage.getItem(FCM_TOKEN_KEY); } catch {}
    if (stored) {
      await deleteToken(authToken, stored);
      try { localStorage.removeItem(FCM_TOKEN_KEY); } catch {}
    }
    return;
  }

  try {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== "granted") return;
    await bindListeners();
    await PushNotifications.register(); // -> "registration" event -> putToken()
  } catch (e) {
    console.warn("[push] register flow failed", e);
  }
}

// Remove this device's token from the backend (called on logout, before the JWT clears).
export async function unregisterNativePush(authToken) {
  if (!isNativePlatform()) return;
  let stored = null;
  try { stored = localStorage.getItem(FCM_TOKEN_KEY); } catch {}
  if (stored) {
    await deleteToken(authToken, stored);
    try { localStorage.removeItem(FCM_TOKEN_KEY); } catch {}
  }
}
